// Sidebar tree helpers.
// The tree is the internal portal's customer category hierarchy (any depth),
// limited to categories actually assigned to an owned model plus their
// ancestors. Models with no assignments (or that the catalog does not know)
// land in a dummy top-level Uncategorized bucket. A model can sit in several
// categories at once; it shows while any of them is on.
// Only leaf keys are stored in checkedKeys; parents conduct from them, so a
// category counts as on while it is fully or partially checked. That set is
// every checked leaf plus its ancestors, which is what makeProductMatcher
// computes.

const UNCATEGORIZED_ID = 'uncat';
const UNCATEGORIZED_KEY = 'cat:uncat';
const UNCATEGORIZED_TITLE = 'Uncategorized';

const keyFor = (id) => `cat:${id}`;

const parentIdOf = (cat) => (
    cat.parent_id === null || cat.parent_id === undefined ? null : String(cat.parent_id)
);

const byIdMap = (customerCategories) => {
    const byId = new Map();
    (customerCategories || []).forEach((cat) => {
        byId.set(String(cat.customer_category_id), cat);
    });
    return byId;
};

// assignments to categories the feed does not know are ignored
const assignedIds = (product, byId) => (
    (product.customer_category_ids || [])
        .map((id) => String(id))
        .filter((id) => byId.has(id))
);

const selfAndAncestors = (id, byId, into) => {
    let current = id;
    while (current !== null && byId.has(current) && !into.has(current)) {
        into.add(current);
        current = parentIdOf(byId.get(current));
    }
};

export const buildProductTree = (products, customerCategories) => {
    const byId = byIdMap(customerCategories);

    // categories directly assigned to an owned product
    const used = new Set();
    let hasUncategorized = false;

    (products || []).forEach((product) => {
        const ids = assignedIds(product, byId);
        if (!ids.length) {
            hasUncategorized = true;
        }
        ids.forEach((id) => used.add(id));
    });

    // plus their ancestors, even when nothing is assigned to those directly
    const include = new Set();
    used.forEach((id) => selfAndAncestors(id, byId, include));

    const childrenOf = new Map();
    include.forEach((id) => {
        const parentId = parentIdOf(byId.get(id));
        // a missing parent (inactive or unknown) puts the node at the top level
        const parentKey = parentId !== null && include.has(parentId) ? parentId : null;
        if (!childrenOf.has(parentKey)) {
            childrenOf.set(parentKey, []);
        }
        childrenOf.get(parentKey).push(id);
    });

    childrenOf.forEach((list) => list.sort((a, b) =>
        String(byId.get(a).customer_category_label).toLowerCase()
            .localeCompare(String(byId.get(b).customer_category_label).toLowerCase())
    ));

    const makeNodes = (parentId) => (childrenOf.get(parentId) || []).map((id) => ({
        title: byId.get(id).customer_category_label,
        key: keyFor(id),
        children: makeNodes(id),
    }));

    const tree = makeNodes(null);

    // Uncategorized always sits last
    if (hasUncategorized) {
        tree.push({
            title: UNCATEGORIZED_TITLE,
            key: UNCATEGORIZED_KEY,
            children: [],
        });
    }

    return tree;
};

export const getLeafKeys = (treeData) => {
    const walk = (nodes) => nodes.flatMap((node) => (
        node.children && node.children.length ? walk(node.children) : [node.key]
    ));
    return walk(treeData || []);
};

export const getAllLeafKeys = (products, customerCategories) => (
    getLeafKeys(buildProductTree(products, customerCategories))
);

// Returns a (product) => boolean for the current checkbox state.
export const makeProductMatcher = (checkedKeys, customerCategories) => {
    const byId = byIdMap(customerCategories);

    const active = new Set();
    (checkedKeys || []).forEach((key) => {
        if (!String(key).startsWith('cat:')) {
            return;
        }

        const id = String(key).slice(4);
        if (id === UNCATEGORIZED_ID) {
            active.add(UNCATEGORIZED_ID);
        } else {
            selfAndAncestors(id, byId, active);
        }
    });

    return (product) => {
        const ids = assignedIds(product, byId);
        if (!ids.length) {
            return active.has(UNCATEGORIZED_ID);
        }
        return ids.some((id) => active.has(id));
    };
};
